import { useCallback, KeyboardEvent, RefObject } from 'react'
import { useRouter } from 'next/router'
import omit from 'lodash/omit'
import { PickerHandle } from 'rsuite'

import useUrls from '../../../../../services/useUrls'
import { QueryParam } from '../../../../../utils/constants'

interface UseSearchHandlersProps {
  searchTerm: string
  setSearchTerm: (term: string) => void
  refine: (query: string) => void
  externalSearchCallback: () => void
  searchInputRef: RefObject<PickerHandle>
  locale: string
}

const useSearchHandlers = ({
  searchTerm,
  setSearchTerm,
  refine,
  externalSearchCallback,
  searchInputRef,
  locale,
}: UseSearchHandlersProps) => {
  const { pushT } = useUrls()
  const { asPath, query } = useRouter()

  // When clearing search, pass all the queries apart from the search and filters
  const queriesWithoutSearchAndFilters = omit(query, [QueryParam.search, QueryParam.filters])

  const searchInputElement = searchInputRef.current?.root?.getElementsByTagName('input')[0]

  const pushNewPathOnSearchUpdate = (urlPath: string, value: string) => {
    // If search value was passed, apply timeout to persist the value on the search input
    if (value) {
      setTimeout(() => {
        setSearchTerm(value)
        refine(value)
      }, 0)
    }

    pushT({
      pathname: urlPath,
      query: {
        ...(value
          ? { [QueryParam.search]: value }
          : queriesWithoutSearchAndFilters
        ),
      },
    },
    undefined,
    { shallow: true, scroll: true })
  }

  const triggerSearch = (value?: string) => {
    pushNewPathOnSearchUpdate('/products', value ?? searchTerm)
    externalSearchCallback()
    searchInputElement?.click()
    searchInputElement?.blur()
  }

  const handleEnterKeyPress = useCallback((event: KeyboardEvent<Element>) => {
    if (event.key !== 'Enter') return
    triggerSearch()
  }, [locale, searchTerm])

  const handleClearSearchClick = () => {
    setSearchTerm('')
    refine('')
    searchInputElement?.click()
    searchInputElement?.blur()

    const [urlWithoutParams] = asPath.split('?')
    pushNewPathOnSearchUpdate(urlWithoutParams, '')
  }

  const handleChange = (val: string) => {
    setSearchTerm(val)
    refine(val)
  }

  const handleSearchClick = () => {
    if (searchTerm) {
      triggerSearch()
    }
  }

  const handleMenuItemClick = (label: string) => {
    triggerSearch(label)
  }

  const handleSupplierClick = (supplierId: string) => {
    externalSearchCallback()
    searchInputElement?.click()
    searchInputElement?.blur()
    // Get existing supplier IDs from current query
    const existingSupplierIds = Array.isArray(query.f_supid)
      ? query.f_supid
      : [query.f_supid]
    const supplierIdString = existingSupplierIds.join('-')
    const currentSupplierIds = supplierIdString ? supplierIdString.split('-') : []

    // Add new supplier ID if not already present
    const updatedSupplierIds = [...currentSupplierIds]
    if (!currentSupplierIds.includes(supplierId)) {
      updatedSupplierIds.push(supplierId)
    }
    pushT({
      pathname: '/products',
      query: {
        ...queriesWithoutSearchAndFilters,
        f_supid: updatedSupplierIds.join('-'),
      },
    },
    undefined,
    { shallow: true })
  }

  return {
    handleEnterKeyPress,
    handleClearSearchClick,
    handleChange,
    handleSearchClick,
    handleMenuItemClick,
    triggerSearch,
    handleSupplierClick,
  }
}

export default useSearchHandlers
